"use client";
import { invalidateReservationQueries } from "@/features/reserve/components/Reserve/helpers";
import { ReserveProviderProps } from "@/features/reserve/components/Reserve/types";
import { useI18n } from "@/lib/hooks/useI18n";
import { useRedirectAfterReserve } from "@/features/reserve/hooks/useRedirectAfterReserve";
import { useReserveResources } from "@/features/reserve/hooks/useReserveResources";
import { useReserveOfficeAvailabilityErrors } from "@/features/reserve/hooks/useReserveOfficeAvailabilityErrors";
import { canScheduleOthers } from "@/lib/permissions";
import {
  convertTZ,
  getMinDateAllowedToReserve,
  getTimeForReservation
} from "@/lib/utils";
import {
  getDurationLabel,
  getDurationOptions,
  getOfficeHealthReservationRestrictions,
  noParkingResource
} from "@/features/reserve/utils/utils-reserve";
import {
  type SaveReservationData,
  editReservations,
  saveReservations
} from "@/features/reserve/utils/utils-reserve";
import { isValidEmail } from "@/lib/validation/email";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import type { IResource } from "@gettactic/api";
import { type ReserveFormDefaultValues } from "@gettactic/components/src/reserve/types";
import { useBooleanState } from "@gettactic/helpers";
import { getDefaultsHoursFromOffice } from "@gettactic/helpers";
import { useFloors } from "@gettactic/hooks";
import { useAuthenticated } from "@gettactic/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReserveFormContext } from "@cmp-src/reserve/ReverseFormContext";
import { format, subMinutes } from "date-fns";
import React, { PropsWithChildren, useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { isWorkplaceRequestBufferEnabled } from "@gettactic/helpers/src/featureFlags";
import type { ExternalGuestInfo } from "@/features/visitors/components/PostReservationVisitorPrompt";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";

export const ReserveFormProvider: React.FC<
  PropsWithChildren<ReserveProviderProps> & {
    onRoomChange?: (roomId: string | null) => void;
    onOrganizerChange?: (organizerId: string | null) => void;
    organizerRequiresDelegatedAccess?: boolean;
    hasMSCalendar?: boolean;
    hasGoogleCalendar?: boolean;
    wpRequestsRef?: React.RefObject<WorkplaceRequestsRef | null>;
    requireDelegatedPermission?: boolean;
    isOnReservationModal?: boolean;
    shouldLoadResources?: boolean;
    onExternalGuestsDetected?: (info: ExternalGuestInfo) => void;
  }
> = ({ children, wpRequestsRef, onExternalGuestsDetected, ...reserveProps }) => {
  const {
    floorId,
    resourceType: _resourceType,
    start,
    resource,
    time,
    duration,
    onSuccess,
    onStartTimeChange,
    onDurationChange,
    onRoomChange,
    onOrganizerChange,
    organizerRequiresDelegatedAccess,
    hasMSCalendar,
    hasGoogleCalendar,
    attenders,
    title,
    description,
    owner,
    schedule,
    isOnReservationModal,
    shouldLoadResources,
    isAdvancedRecurringOpenByDefault = false,
    recurring: initialRecurring,
    onlineMeetingProvider: initialOnlineMeetingProvider
  } = reserveProps;
  const resourceType = _resourceType || "workspace";
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);
  const { setAfterReserve } = useRedirectAfterReserve();

  const [selectedOffice, setSelectedOffice] = React.useState(
    reserveProps.office ?? currentOffice
  );
  const [filterFloor, setFilterFloor] = React.useState<string | null>(
    floorId ?? authenticatedUser.defaultAreaId
  );
  const isLoading = useBooleanState();
  const [error, setError] = React.useState("");

  const [enableAdminReservation, setEnableAdminReservation] = React.useState(
    !!owner
  );

  const queryClient = useQueryClient();
  const { floors } = useFloors(selectedOffice?.id ?? "");
  const { rooms: selectedRooms } = useRoomSelection();

  // We should validate that the floor filter selected
  // exists among the current floor list
  React.useEffect(() => {
    if (!floors || !filterFloor) {
      return;
    }
    if (!floors.find((x) => x.id === filterFloor)) {
      setFilterFloor(null);
    }
  }, [floors, filterFloor]);

  // Resource Types
  const isMeetingRoom = resourceType === "meeting_room";
  const isDesk = resourceType === "workspace" || resourceType === "desk";
  const isParking =
    resourceType === "parking_space" || resourceType === "parking_lot";
  const allowOthers = canScheduleOthers(authenticatedUser);

  // If we aren't given a start date, use the current date/time in the selected office's timezone
  const startOfficeDate: Date =
    start || convertTZ(new Date(), selectedOffice.time_zone);
  const offices = authenticatedUser.offices?.offices ?? [];
  const { meetingRoomDuration, deskDuration } =
    getDefaultsHoursFromOffice(selectedOffice);

  // Default or last accessed values
  const lastAccessedResourceId = isMeetingRoom
    ? authenticatedUser?.user?.organization_user
        .last_accessed_meeting_room_resource_id || null
    : authenticatedUser?.user?.organization_user
        .last_accessed_desk_resource_id || null;
  const lastAccessedParkingResourceId =
    authenticatedUser?.user?.organization_user
      .last_accessed_parking_resource_id || noParkingResource.id;
  const currentOfficeTz = selectedOffice.time_zone;
  const currentOfficeId = selectedOffice.id;
  const currentOfficeDayIndex = convertTZ(new Date(), currentOfficeTz).getDay();

  // Track if duration was custom selected from props or changed by user
  const userChangedDuration = React.useRef(false);
  const userStartTimeChanged = React.useRef(false);

  // For meeting rooms: use duration prop if provided and different from default, otherwise use office default
  // For other types: use duration prop if provided, otherwise use office default
  // This allows map filter's end time to be used for rooms when it differs from default
  const defaultMeetingRoomDuration = meetingRoomDuration * 60;
  const initialDuration = isMeetingRoom
    ? duration && Math.abs(duration - defaultMeetingRoomDuration) > 1
      ? duration // Use provided duration if it's different from default
      : defaultMeetingRoomDuration // Otherwise use default
    : duration
      ? duration
      : deskDuration * 60;

  // If duration is specified in props and different from default,
  // mark as user-changed, so it doesn't get reset on office change
  React.useEffect(() => {
    const isDurationPropDifferent =
      duration &&
      duration !== deskDuration * 60 &&
      duration !== meetingRoomDuration * 60;
    const isTimePropDifferent = start && start !== startOfficeDate;
    if (isDurationPropDifferent || isTimePropDifferent) {
      userChangedDuration.current = true;
      userStartTimeChanged.current = true;
    }
  }, []);

  const defaultValues: ReserveFormDefaultValues = {
    title: title || "",

    resource_id: resource?.id ?? lastAccessedResourceId,
    parking_resource_id: lastAccessedParkingResourceId,
    start: startOfficeDate,
    time: getTimeForReservation(startOfficeDate, time, currentOffice),
    duration: initialDuration,
    description: description || "",

    // TODO: Fix this type
    users: attenders?.map(
      (user) => user.id
    ) as ReserveFormDefaultValues["users"]
  };

  if (!defaultValues.start || !(defaultValues.start instanceof Date)) {
    defaultValues.start = getMinDateAllowedToReserve(selectedOffice.time_zone);
  }

  const form = useForm<SaveReservationData>({
    defaultValues: {
      title: defaultValues.title,
      weekDates: [defaultValues.start],
      time: defaultValues.time,
      duration: { value: defaultValues.duration },
      resource: resource ?? undefined,
      users: attenders?.map((user) => ({
        label: user.name || user.email,
        value: user.id
      })) ?? [],
      description: defaultValues.description,
      visibility: schedule?.visibility ?? undefined,
      online_meeting: initialOnlineMeetingProvider
        ? { provider: initialOnlineMeetingProvider }
        : null,
      customMeetingLink: "",
      recurring: initialRecurring
    }
  });

  // Update users when attenders prop changes (e.g. on duplicate)
  React.useEffect(() => {
    if (attenders) {
      const userOptions = attenders.map((user) => ({
        label: user.name || user.email,
        value: user.id
      }));
      form.setValue("users", userOptions);
    }
  }, [attenders, form]);

  // Watching form values due to field dependencies
  const weekDates = form.watch("weekDates");
  const users = (form.watch("users") ?? []).map((x) => x.value);
  const organizer = form.watch("organizer");
  const currentDuration = form.watch("duration", {
    value: defaultValues.duration
  });
  const formTime = form.watch("time");

  const weekDatesStr = weekDates.map((weekDate: Date) =>
    format(weekDate, "yyyy-MM-dd")
  );
  const [durationMins, setDurationMins] = React.useState<number>(
    () => defaultValues.duration
  );
  const [startTime, setStartTime] = React.useState<Date>(
    () => defaultValues.time
  );

  const prevOfficeId = React.useRef(selectedOffice.id);

  const handleDurationChange = React.useCallback(
    (newDuration: number | React.SetStateAction<number>) => {
      userChangedDuration.current = true;
      const duration =
        typeof newDuration === "function"
          ? newDuration(durationMins)
          : newDuration;
      setDurationMins(duration);
      onDurationChange?.(duration);
    },
    [durationMins, onDurationChange]
  );

  const lastPushedTime = React.useRef<number | null>(null);

  const handleStartTimeChange = React.useCallback(
    (newStartTime: Date | React.SetStateAction<Date>) => {
      userStartTimeChanged.current = true;
      setStartTime(newStartTime);
      if (newStartTime instanceof Date) {
        lastPushedTime.current = newStartTime.getTime();
        onStartTimeChange?.(newStartTime);
      }
    },
    [startTime, onStartTimeChange]
  );

  // Watch for changes in form time field and update startTime state
  React.useEffect(() => {
    if (formTime && formTime instanceof Date) {
      // Avoid infinite loops if formTime is same as startTime
      if (startTime && formTime.getTime() === startTime.getTime()) return;

      setStartTime(formTime);
      lastPushedTime.current = formTime.getTime();
      onStartTimeChange?.(formTime);
    }
  }, [formTime, onStartTimeChange, startTime]);

  // Keep startTime (and thus MapReserveCard's tracking) in sync when date changes in the form
  React.useEffect(() => {
    if (weekDates?.[0] && startTime) {
      const newDateTime = getTimeForReservation(
        weekDates[0],
        startTime,
        selectedOffice
      );
      if (newDateTime.getTime() !== startTime.getTime()) {
        setStartTime(newDateTime);
        lastPushedTime.current = newDateTime.getTime();
        onStartTimeChange?.(newDateTime);
         // Also update the form's time value to stay in sync with the new date
         form.setValue("time", newDateTime);
      }
    }
  }, [weekDates, startTime, onStartTimeChange, selectedOffice, form]);

  // Sync local state when external time prop changes
  React.useEffect(() => {
    if (time) {
      const timeMs = time.getTime();
      // If this is the value we just pushed, ignore it to prevent loops/resets
      if (lastPushedTime.current === timeMs) {
        return;
      }

      setStartTime((prev) => {
        if (prev.getTime() !== timeMs) {
          form.setValue("time", time);
          form.setValue("weekDates", [time]);
          return time;
        }
        return prev;
      });
    }
  }, [time, form]);

  React.useEffect(() => {
    const { meetingRoomDuration, deskDuration } =
      getDefaultsHoursFromOffice(selectedOffice);
    const officeDate = convertTZ(new Date(), selectedOffice.time_zone);
    const newDefaultTime = getTimeForReservation(
      officeDate,
      time,
      selectedOffice
    );

    const isOfficeChanged = prevOfficeId.current !== selectedOffice.id;
    // Only update office ID after checking if it changed
    prevOfficeId.current = selectedOffice.id;

    const isTimeChangedByUser = userStartTimeChanged.current;

    // Update start time if it hasn't been changed or office changed
    if (!isTimeChangedByUser && isOfficeChanged) {
      form.setValue("time", newDefaultTime);
      setStartTime(newDefaultTime);
    }

    // Only update duration if:
    // 1. It's a meeting room AND user hasn't manually changed duration (or passed custom duration)
    // 2. Office changed AND user hasn't manually changed duration
    // 3. Duration in map filter is different from current duration
    const isDurationChangedByUser = userChangedDuration.current;
    if (
      (isMeetingRoom && !isDurationChangedByUser) ||
      (isOfficeChanged && !isDurationChangedByUser && !isTimeChangedByUser) ||
      defaultValues.duration !== durationMins
    ) {
      const newDuration = defaultValues.duration;
      setDurationMins(newDuration);
      form.setValue("duration", { value: newDuration });
    }
  }, [selectedOffice, isMeetingRoom, form, defaultValues.duration]);

  // When reserving on behalf of another user, use _that_ person's resource permissions & assignments
  const userIdForResources = enableAdminReservation
    ? isMeetingRoom
      ? (organizer?.value ?? null)
      : users[0]
    : null;
  const { resourcesList, office, parkingResourcesList } = useReserveResources({
    resourceType,
    officeId: currentOfficeId,
    durationMins,
    startTime,
    weekDatesStr,
    filterParent: filterFloor,
    organizer: isValidEmail(userIdForResources) ? null : userIdForResources,
    enabled: shouldLoadResources ?? true
  });
  const { officeAvailabilityErrors } = useReserveOfficeAvailabilityErrors({
    officeId: currentOfficeId,
    startTime,
    weekDatesStr,
    durationMins,
    filterParent: filterFloor,
    organizer: isValidEmail(userIdForResources) ? null : userIdForResources,
    enabled: shouldLoadResources ?? true
  });

  // If we pass a resource to the component, we show that resource EVEN if it is not available eg in the list
  // This allows schedulers/admins to choose a resource from the map, even when they don't have permission to reserve it themselves.
  // It also facilitates meeting room reservations that are not perfectly aligned with the default map filter times.
  const { updatedResourcesList, defaultResource } = React.useMemo(() => {
    let updatedResourcesList = resourcesList;
    let defaultResource: IResource | null =
      defaultValues.resource_id && resourcesList
        ? (resourcesList.find(
            (resource) => resource.id === defaultValues.resource_id
          ) ?? null)
        : null;
    if (resource && resourcesList) {
      defaultResource = resource;
      // If the default resource is not available, add it to the list so it can be selected. Omit if it is already in the list.
      if (!resourcesList.find((x) => x.id === resource.id)) {
        updatedResourcesList = [...resourcesList, resource];
      }
    }
    return {
      updatedResourcesList,
      defaultResource
    };
  }, [resourcesList, resource, defaultValues]);

  // Sync the resource form field when defaultResource becomes available
  // (e.g. after resourcesList loads asynchronously)
  React.useEffect(() => {
    if (defaultResource) {
      const currentVal = form.getValues("resource");
      if (!currentVal || currentVal.id !== defaultResource.id) {
        form.setValue("resource", defaultResource);
      }
    }
  }, [defaultResource, form]);

  const durationOptions = React.useMemo(
    () => getDurationOptions(startTime),
    [startTime, currentOfficeTz]
  );
  const currentResource = form.watch("resource", defaultResource ?? undefined);
  useEffect(() => {
    onRoomChange?.(
      resourceType === "meeting_room" ? currentResource?.id : null
    );
  }, [currentResource?.id, resourceType, onRoomChange]);

  // The organizer field is only filled once its async user lookup resolves, so until then
  // the owner it will be filled with is the organizer. Reporting nobody meanwhile checks the
  // current user, whose Connect Outlook screen unmounts this form before it catches up
  const organizerId = organizer === undefined ? owner?.id : organizer?.value;
  // Only a meeting room booked on behalf of someone has an organizer other than the current user
  const onBehalfOrganizerId =
    enableAdminReservation && isMeetingRoom ? (organizerId ?? null) : null;
  useEffect(() => {
    onOrganizerChange?.(
      isValidEmail(onBehalfOrganizerId) ? null : onBehalfOrganizerId
    );
  }, [onBehalfOrganizerId, onOrganizerChange]);

  const isUserHealthRestricted = !enableAdminReservation
    ? getOfficeHealthReservationRestrictions(
        authenticatedUser,
        office ?? undefined
      )
    : false;

  const submitMutation = useMutation({
    mutationFn: async (data: SaveReservationData) => {
      const authUserId = authenticatedUser?.user?.id;
      let savedScheduleIds: string[] = [];
      // If the user is not allowed to schedule in the past & the time selected is more than 5 minutes in the past, throw an error message
      // if (
      //   isNoPastReservations(authenticatedUser) &&
      //   isPast(subMinutes(data.time, 5))
      // ) {
      //   setError(
      //     'Reservations in the past are disabled. Please choose a time in the future.'
      //   );
      //   throw new Error();
      // }

      if (!office || !authUserId) {
        // just to avoid ts warning
        throw new Error("Not required params");
      }
      isLoading.true();

      if (!data.duration?.value || data.duration.value < 5) {
        form.setError("duration", {
          type: "manual",
          message: "Duration must be at least 5 minutes"
        });
        throw new Error();
      }

      // Apply buffer time if workplace requests have buffer settings
      let adjustedData = data;
      const bufferEnabled = isWorkplaceRequestBufferEnabled(authenticatedUser);
      if (bufferEnabled && currentBufferSettings) {
        if (
          currentBufferSettings.bufferBefore > 0 ||
          currentBufferSettings.bufferAfter > 0
        ) {
          // Adjust start time by subtracting buffer before
          const adjustedTime = subMinutes(
            data.time,
            currentBufferSettings.bufferBefore
          );
          // Adjust duration by adding both buffers
          const adjustedDuration =
            (data.duration?.value || 0) +
            currentBufferSettings.bufferBefore +
            currentBufferSettings.bufferAfter;

          adjustedData = {
            ...data,
            time: adjustedTime,
            duration: { value: adjustedDuration }
          };
        }
      }

      if (schedule) {
        // Edit mode: update the existing reservation
        // Ensure users are populated for desk edits
        if (adjustedData.users.length === 0) {
          const scheduleUsers = schedule.slots
            .filter(
              (s) =>
                s.resource.type !== "parking_lot" &&
                s.resource.type !== "parking_space"
            )
            .map((s) => ({ value: s.user.id }));
          if (scheduleUsers.length > 0) {
            adjustedData = { ...adjustedData, users: scheduleUsers };
          } else {
            adjustedData = {
              ...adjustedData,
              users: [{ value: authUserId }]
            };
          }
        }

        const result = await editReservations(
          schedule,
          adjustedData,
          false,
          templates.displayDate,
          authenticatedUser.slug ?? ""
        );

        if (wpRequestsRef?.current) {
          try {
            await wpRequestsRef.current.submitWorkplaceRequests({
              scheduleId: schedule.id,
              ownerUserId: userIdForResources ?? authUserId
            });
          } catch {
            // Log error but don't block the reservation success
          }
        }

        if (result.hasError && result.message) {
          setError(result.message);
          throw new Error();
        }
        savedScheduleIds = [schedule.id];
      } else {
        // Create mode: create a new reservation
        // Rooms picked on the map beyond the one the form is built around: the
        // reservation holds all of them at once.
        const extraRooms = isMeetingRoom
          ? selectedRooms.filter((room) => room.id !== adjustedData.resource?.id)
          : [];

        const { hasError, message, scheduleIds } = await saveReservations(
          adjustedData,
          {
            organizerId: data.organizer?.value ?? null,
            isMeetingRoom,
            authUserId,
            office,
            rooms: extraRooms,
            // allowOthers && the admin has chosen a user
            allowOthers: enableAdminReservation,
            timeZone: office.time_zone,
            officeId: currentOfficeId,
            noParkingResource,
            dateFormat: templates.displayDate,
            orgSlug: authenticatedUser.slug || ""
          }
        );

        // Store the created schedule IDs for workplace requests
        if (scheduleIds && scheduleIds.length > 0) {
          const scheduleId = scheduleIds[0];
          if (wpRequestsRef?.current) {
            try {
              const result =
                await wpRequestsRef.current.submitWorkplaceRequests({
                  scheduleId,
                  ownerUserId: userIdForResources ?? authUserId
                });
              if (!result.success) {
                // Log error but don't block the reservation success
              }
            } catch {
              // Log error but don't block the reservation success
            }
          }
        }

        if (hasError && message) {
          setError(message);
          throw new Error();
        }
        savedScheduleIds = scheduleIds ?? [];
      }
      return { scheduleIds: savedScheduleIds };
    },

    onError: () => {
      isLoading.false();
    },

    onSuccess: async (data, variables) => {
      // This should return false for 95% of users currently.
      // TODO: Investigate if there is a less expensive way to determine if we should call this.
      setAfterReserve(selectedOffice.id);

      // After a new meeting room reservation, detect external email attendees
      // for visitor management pre-registration
      if (isMeetingRoom && !schedule && onExternalGuestsDetected) {
        const externalEmails = variables.users
          .map((u) => u.value)
          .filter((v) => isValidEmail(v));
        if (externalEmails.length > 0) {
          onExternalGuestsDetected({
            emails: externalEmails,
            officeId: currentOfficeId,
            date: variables.weekDates[0],
            time: variables.time
          });
        }
      }

      onSuccess?.(data.scheduleIds);
    },

    onSettled: () => {
      invalidateReservationQueries(queryClient);
      isLoading.false();
    }
  });

  const [reservationDurationLabel, setReservationDurationLabel] =
    React.useState(getDurationLabel(currentDuration.value));
  React.useEffect(() => {
    setReservationDurationLabel(getDurationLabel(currentDuration.value));
  }, [currentDuration.value]);

  // Buffer settings state for workplace requests
  const [currentBufferSettings, setCurrentBufferSettings] = React.useState<{
    bufferBefore: number;
    bufferAfter: number;
  }>({ bufferBefore: 0, bufferAfter: 0 });

  const submitReservation: SubmitHandler<SaveReservationData> = async (
    data
  ) => {
    // The backend rejects it, and Enter can submit even with the button disabled
    if (organizerRequiresDelegatedAccess) {
      return;
    }
    submitMutation.mutate(data);
  };

  const totalParking =
    (office?.total_parking_lots ?? 0) + (office?.total_parking_spaces ?? 0);

  const allowParking = isDesk && !isParking && !!office && totalParking > 0;

  const isSubmitDisabled =
    isLoading.state ||
    isUserHealthRestricted ||
    !updatedResourcesList ||
    !currentResource ||
    !!organizerRequiresDelegatedAccess;

  return (
    <ReserveFormContext.Provider
      value={{
        ...reserveProps,
        resourceType,
        form,
        office,
        isMeetingRoom,
        weekDates,
        currentOfficeTz,
        startTime,
        defaultValues,
        currentOfficeId,
        durationOptions,
        defaultResource,
        durationMins,
        updatedResourcesList,
        parkingResourcesList,
        allowParking,
        floors,
        filterFloor,
        selectedOffice,
        currentOffice,
        offices,
        users,
        organizer,
        errorMessage: error,
        officeAvailabilityErrors,
        enableAdminReservation,
        allowOthers,
        isLoading: isLoading.state,
        isUserHealthRestricted,
        isSubmitDisabled,
        reservationDurationLabel,
        currentResource,
        hasMSCalendar,
        hasGoogleCalendar,
        wpRequestsRef,
        currentBufferSettings,
        setCurrentBufferSettings,
        // Actions
        setStartTime: handleStartTimeChange, // Use the new handler that tracks user changes
        setDurationMins: handleDurationChange, // Use the new handler that tracks user changes
        setFilterFloor,
        setSelectedOffice,
        setEnableAdminReservation,
        submitReservation,
        isAdvancedRecurringOpenByDefault,

        isOnReservationModal: isOnReservationModal ?? false,
        isReservationCardIsOpen: reserveProps.isReservationCardIsOpen ?? false
      }}
    >
      {children}
    </ReserveFormContext.Provider>
  );
};

export const useReserveForm = () => {
  const context = React.useContext(ReserveFormContext);
  if (!context) {
    throw new Error("useReserveForm must be used within a ReserveFormProvider");
  }
  return context;
};
